import React from 'react';
import { User } from '../context/AuthContext';
import { MODULE_URL_OVERRIDES } from '../utils/config';

export interface ModuleSummary {
    id: string;
    name?: string;
    description?: string;
    icon?: string;
    color?: string;
    status: string;
    redirect_url: string | null;
}

/** Gordian admin, derived exactly like the backend does (role string OR flag). */
export const isGordianAdmin = (
    u: User | { role?: string; is_admin?: boolean } | null
): boolean =>
    u?.role === 'admin' ||
    u?.is_admin === true;

/**
 * Where a non-admin gets dropped when auto-entering: RFQ module when active,
 * or single available module for B2C accounts.
 */
export const autoEnterTarget = <T extends ModuleSummary>(mods: T[]): T | undefined => {
    const active = mods.filter((m) => m.status === 'active');
    return active.find((m) => m.id === 'rfq') || (active.length === 1 ? active[0] : undefined);
};

/**
 * Validate redirect targets to prevent open redirect and token leakage.
 * Only allows same-origin paths, localhost in dev, or known Gordian domains/hosts.
 */
export const getSafeRedirectUrl = (target: string | null): string | null => {
    if (!target) return null;

    // Safe relative URL
    if (target.startsWith('/') && !target.startsWith('//') && !target.includes('\\')) {
        return target;
    }

    try {
        const parsed = new URL(target, window.location.origin);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
            return null;
        }

        const host = parsed.hostname.toLowerCase();

        // Same origin host
        if (host === window.location.hostname.toLowerCase()) {
            return target;
        }

        // Local development hosts
        if (host === 'localhost' || host === '127.0.0.1') {
            return target;
        }

        // Gordian production/staging domain and subdomains
        if (host === 'gordiananalytics.com' || host.endsWith('.gordiananalytics.com')) {
            return target;
        }

        // Explicitly configured module hosts
        const knownModuleHosts = Object.values(MODULE_URL_OVERRIDES)
            .map((url) => {
                try {
                    return new URL(url).hostname.toLowerCase();
                } catch {
                    return null;
                }
            })
            .filter(Boolean);

        if (knownModuleHosts.includes(host)) {
            return target;
        }
    } catch {
        return null;
    }

    return null;
};

/** A ?redirect= target handed over by another module, if any and if safe. */
export const pendingRedirect = (): string | null => {
    const raw = new URLSearchParams(window.location.search).get('redirect');
    return getSafeRedirectUrl(raw);
};

export const getIconSvg = (iconName?: string) => {
    switch (iconName) {
        case 'request_quote':
            return (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                </svg>
            );
        case 'inventory':
            return (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                    <line x1="12" y1="22.08" x2="12" y2="12" />
                </svg>
            );
        case 'receipt':
            return (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z" />
                    <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
                    <path d="M12 6v2m0 8v2" />
                </svg>
            );
        case 'description':
            return (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <line x1="10" y1="9" x2="8" y2="9" />
                </svg>
            );
        case 'mail':
            return (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <polyline points="3 7 12 13 21 7" />
                </svg>
            );
        case 'language':
            // Web Scraper (globe)
            return (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="9" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <path d="M12 3a14.5 14.5 0 0 1 0 18a14.5 14.5 0 0 1 0-18" />
                </svg>
            );
        case 'article':
            // Logs
            return (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="4" y="3" width="16" height="18" rx="2" />
                    <line x1="8" y1="8" x2="16" y2="8" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                    <line x1="8" y1="16" x2="13" y2="16" />
                </svg>
            );
        case 'vpn_key':
        case 'key':
            // Gordian Key
            return (
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <circle cx="7.5" cy="15.5" r="5.5" />
                    <path d="m21 2-9.6 9.6" />
                    <path d="m15.5 7.5 3 3L22 7l-3-3" />
                </svg>
            );
        default:
            return (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                </svg>
            );
    }
};
